// src/layouts/openPages/live/Live.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getThing, startThingUpdates, stopUpdates } from './LiveApis';
import Openlayer from './Openlayer';
import {
  Modal,
  Button,
  Box,
  Typography,
  Container,
  Paper,
  Alert,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

function Live() {
  const [thingModel, setThingModel] = useState(null);
  const [error, setError] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [fromMeLocation, setFromMeLocation] = useState({ lat: 0, lon: 0, flag: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const [browsmapLink, setBrowsmapLink] = useState('');
  const [currentLocation, setCurrentLocation] = useState(null);
  const [updateLocation, setUpdateLocation] = useState(null);
  const [selectedStopId, setSelectedStopId] = useState('');
  const mapRef = useRef(null);

  // Extract URL params
  useEffect(() => {
    const authParams = new URLSearchParams(window.location.search);
    const weblink = `https://test.dhundhoo.com/live_ol_google?apiKey=${authParams.get(
      'apiKey'
    )}&thingId=${authParams.get('thingId')}`;
    setBrowsmapLink(weblink);

    getThing(
      (data) => {
        // Sort stops by sequence
        if (data.currentJourney?.sequence) {
          const keys = Object.keys(data.currentJourney.sequence);
          if (keys.length > 0) {
            const ordered = {};
            keys.sort((a, b) => parseInt(a, 10) - parseInt(b, 10))
              .forEach((key, index) => {
                ordered[key] = data.currentJourney.sequence[key];
                if (data.stops?.[ordered[key]]) {
                  data.stops[ordered[key]].index = index;
                }
              });
            data.currentJourney.sequence = ordered;
          }
        }

        setThingModel(data);
        setError(null);
        if (data.latest?.GPS?.location) {
          setCurrentLocation(data.latest.GPS.location);
        }
        startThingUpdates((loc) => setUpdateLocation(loc));
      },
      (err) => setError(err)
    );

    const timer = setTimeout(() => setIsLoading(true), 5000);
    return () => {
      clearTimeout(timer);
      stopUpdates();
    };
  }, []);

  useEffect(() => {
    if (updateLocation) setCurrentLocation(updateLocation);
  }, [updateLocation]);

  // Get user location
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setFromMeLocation({
            lat: pos.coords.latitude,
            lon: pos.coords.longitude,
            flag: 1,
          });
        },
        () => {
          setFromMeLocation({ lat: 0, lon: 0, flag: 1 });
        },
        { enableHighAccuracy: true, timeout: 15000 }
      );
    } else {
      setFromMeLocation({ lat: 0, lon: 0, flag: 1 });
    }
  }, []);

  // Google Maps Distance Modal
  const getData = useCallback(() => {
    if (fromMeLocation.flag !== 1 || !currentLocation) return;

    const v = currentLocation;
    const glink = `https://www.google.com/maps/embed?pb=!1m26!1m12!1m3!1d952990.5372896822!2d77.48398416359312!3d21.08786269702407!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!4m11!3e0!4m4!2s${v.latitude}%2C${v.longitude}!3m2!1d${v.latitude}!2d${v.longitude}!4m4!2s${fromMeLocation.lat}%2C${fromMeLocation.lon}!3m2!1d${fromMeLocation.lat}!2d${fromMeLocation.lon}!5e0!3m2!1sen!2sin!4v1668452361266!5m2!1sen!2sin`;

    setModalVisible(true);
    setTimeout(() => {
      const iframe = document.getElementById('googleiframe');
      if (iframe) iframe.src = glink;
    }, 100);
  }, [currentLocation, fromMeLocation]);

  const openTrackLink = () => {
    const p = new URLSearchParams(window.location.search);
    window.location.href = `/live_lite?thingId=${p.get('thingId')}`;
  };

  const handleCancel = () => setModalVisible(false);

  const handleStopClick = (stopId) => {
    setSelectedStopId(stopId);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Derived values
  const deviceStatus = thingModel?.updateMessage || 'Device connected';
  const isConnected = deviceStatus === 'Device connected';
  const lastVisitedIndex = thingModel?.lastVisitedStopIndex || 0;
  const sequenceKeys = thingModel?.currentJourney?.sequence ? Object.keys(thingModel.currentJourney.sequence) : [];
  const nextStopId = lastVisitedIndex < sequenceKeys.length ? thingModel.currentJourney.sequence[sequenceKeys[lastVisitedIndex]] : null;
  const nextStopName = nextStopId && thingModel?.stops?.[nextStopId]?.locationName;

  return (
      <Box sx={{ width: '100%', py: 2 }}>
      {/* Loading */}
      {thingModel === null && error === null && !isLoading && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
          <Typography ml={2}>Loading tracker...</Typography>
        </Box>
      )}

      {/* Slow load */}
      {thingModel === null && error === null && isLoading && (
        <Paper elevation={3} sx={{ p: 3, textAlign: 'center', mt: 4 }}>
          <Typography variant="h6" gutterBottom>If loading takes too long,</Typography>
          <Typography variant="body1" gutterBottom>
            <a href="#" onClick={openTrackLink} style={{ textDecoration: 'underline', color: '#1976d2' }}>
              click here
            </a>.
          </Typography>
          <Button variant="contained" color="primary" onClick={openTrackLink} sx={{ mt: 2 }}>
            Dhundhoo Lite
          </Button>
        </Paper>
      )}

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          <Typography>{error}</Typography>
          <Typography sx={{ mt: 2 }}>Happy Tracking!</Typography>
          <Button
            color="inherit"
            size="small"
            onClick={() => getThing((tm) => setThingModel(tm), (e) => setError(e))}
            sx={{ mt: 1 }}
          >
            Refresh
          </Button>
        </Alert>
      )}

      {/* Main Content */}
      {!error && currentLocation && thingModel && (
        <Box>
          {/* Map Section */}
          <Paper elevation={2} sx={{ position: 'relative', overflow: 'hidden', borderRadius: 2, mb: 3 }}>
            <div ref={mapRef}>
              <Openlayer
                position={currentLocation}
                thingModel={thingModel}
                selectedStopId={selectedStopId}
                onStopSelect={setSelectedStopId}
                userLocation={fromMeLocation.flag === 1 ? { latitude: fromMeLocation.lat, longitude: fromMeLocation.lon } : null}
              />
            </div>

            {/* Distance Button */}
            <Button
              variant="contained"
              color="secondary"
              onClick={getData}
              disabled={fromMeLocation.flag !== 1}
              sx={{
                position: 'absolute',
                top: 16,
                left: 16,
                zIndex: 10,
                px: 3,
                py: 1,
                color:'#fff',
                fontWeight: 'bold',
                minWidth: 180,
                borderRadius: 2,
                boxShadow: 3,
              }}
            >
              Distance From Me
            </Button>
          </Paper>

          {/* Device Status + Info */}
          <Paper elevation={1} sx={{ p: 2, mb: 3, bgcolor: isConnected ? '#e8f5e9' : '#ffebee' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="subtitle1" fontWeight="bold" color={isConnected ? 'success.main' : 'error.main'}>
                  {deviceStatus}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  <strong>Vehicle:</strong> {thingModel.thing?.name || 'N/A'} |{' '}
                  {/* <strong>Service:</strong> {thingModel.currentJourney?.journeyName || 'N/A'} */}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {/* <strong>Vehicle:</strong> {thingModel.thing?.name || 'N/A'} |{' '} */}
                  <strong>Service:</strong> {thingModel.currentJourney?.journeyName || 'N/A'}
                </Typography>
                {/* {nextStopName && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    <strong>Next Stop:</strong> {nextStopName}
                  </Typography>
                )} */}
              </Box>
              <Button variant="outlined" size="small">
                <a href={browsmapLink} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none',color:'black' }}>
                  Track in Browser
                </a>
              </Button>
            </Box>
          </Paper>

          {/* Stops List */}
          <Paper elevation={1} sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Route Stops
            </Typography>
            <List disablePadding>
              {sequenceKeys.map((seqIndex) => {
                const stopId = thingModel.currentJourney.sequence[seqIndex];
                const stop = thingModel.stops[stopId];
                const index = parseInt(seqIndex, 10);
                const isVisited = index < lastVisitedIndex;
                const isSelected = selectedStopId === stopId;

                return (
                  <React.Fragment key={stopId}>
                    <ListItem
                      button
                      selected={isSelected}
                      onClick={() => handleStopClick(stopId)}
                      sx={{
                        borderRadius: 1,
                        mb: 1,
                        bgcolor: isSelected ? 'primary.50' : 'transparent',
                        '&:hover': { bgcolor: 'grey.100' },
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={1}>
                            {/* {isVisited ? (
                              <CheckCircleIcon fontSize="small" color="success" />
                            ) : (
                              <RadioButtonUncheckedIcon fontSize="small" color="action" />
                            )} */}
                            <Typography fontWeight={isSelected ? 'bold' : 'medium'}>
                              {index + 1}. {stop.locationName}
                            </Typography>
                            {/* {index === lastVisitedIndex && (
                              <Chip label="Next" size="small" color="primary" />
                            )} */}
                          </Box>
                        }
                        secondary={stop.address || null}
                      />
                      <Tooltip title="View on Map">
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleStopClick(stopId); }}>
                          <LocationOnIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </ListItem>
                    {index < sequenceKeys.length - 1 && <Divider />}
                  </React.Fragment>
                );
              })}
            </List>
          </Paper>

          {/* Google Maps Modal */}
          <Modal open={modalVisible} onClose={handleCancel}>
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: { xs: '95%', sm: '80%', md: '70%' },
                maxWidth: 900,
                bgcolor: 'background.paper',
                borderRadius: 2,
                boxShadow: 24,
                p: 3,
              }}
            >
              <Typography variant="h6" gutterBottom>
                Route from You to Vehicle
              </Typography>
              <iframe
                id="googleiframe"
                title="Live Route"
                width="100%"
                height="500"
                style={{ border: 0, borderRadius: 8 }}
                loading="lazy"
                allowFullScreen
              />
              <Box sx={{ mt: 2, textAlign: 'right' }}>
                <Button onClick={handleCancel} variant="outlined">
                  Close
                </Button>
              </Box>
            </Box>
          </Modal>
        </Box>
      )}
    </Box>
  );
}

export default Live;